import { isRejectedWithValue, Middleware } from '@reduxjs/toolkit';
import { BaseQueryApi, FetchArgs } from '@reduxjs/toolkit/query';
import { config } from 'services/config';

const AUTH_ENDPOINTS = new Set(['login', 'mfaVerify', 'verifyTrustedDevice']);

/**
 * APIs whose auth failures must NOT clear the pacs-be session. The copilot
 * service (CopilotChatsAPI) runs against a separate host and can 401 for its
 * own reasons (e.g. MFA policy) on a token that's perfectly valid for pacs-be;
 * letting that bounce the user to /login was a real logout regression.
 */
const NON_CORE_API_PREFIX = 'Copilot';

function fireAndForgetLogout() {
  const token = localStorage.getItem('AccessToken');
  if (!token) return;
  fetch(`${config.BASE_URL}/auth/logout/`, {
    method: 'POST',
    headers: { Authorization: `Token ${token}` },
  }).catch(() => {});
}

export const rtkQueryErrorLogger: Middleware =
  () => (next) => (action: unknown) => {
    if (isRejectedWithValue(action)) {
      const { type, payload, meta } = action as {
        type?: string;
        payload?: { status?: number };
        meta?: { arg?: { endpointName?: string } };
      };
      const status = payload?.status;
      const endpoint = meta?.arg?.endpointName;
      // RTK Query prefixes the action type with the api's reducerPath
      // (e.g. "CopilotChatsAPI/executeQuery/rejected").
      const reducerPath = (type ?? '').split('/')[0];
      const isNonCoreApi = reducerPath.startsWith(NON_CORE_API_PREFIX);

      if (
        (status === 401 || status === 403) &&
        !AUTH_ENDPOINTS.has(endpoint ?? '') &&
        !isNonCoreApi
      ) {
        fireAndForgetLogout();
        localStorage.removeItem('AccessToken');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        window.location.assign('/login');
      }
    }

    return next(action);
  };

export const buildQueryString = <T extends object>(params: T): string => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => query.append(key, String(v)));
      } else {
        query.append(key, String(value));
      }
    }
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

export const prepareHeaders = (
  headers: Headers,
  {
    endpoint,
  }: Pick<
    BaseQueryApi,
    'getState' | 'extra' | 'endpoint' | 'type' | 'forced'
  > & {
    arg: string | FetchArgs;
    extraOptions: unknown;
  }
) => {
  const accessToken = localStorage.getItem('AccessToken');
  /** Pre-MFA login steps must not send a stale session token (same as login). */
  const skipAuth =
    endpoint === 'login' ||
    endpoint === 'mfaVerify' ||
    endpoint === 'verifyTrustedDevice';
  if (accessToken && !skipAuth) {
    headers.set('Authorization', `Token ${accessToken}`);
  }
  return headers;
};
